import {Tile as TileLayer, VectorTile as VectorTileLayer} from 'ol/layer';
import {TileDebug, OSM, XYZ, VectorTile} from 'ol/source';
import {MVT} from 'ol/format';
import {Fill, Stroke, Style, Text} from 'ol/style';
import {createXYZ} from 'ol/tilegrid';
import {apiUrl, env} from './config.js';

const TILE_GRID = createXYZ({ minZoom: 3, maxZoom: 15 });

export function createBasemapLayer(title) {
  return new TileLayer({
    source: new OSM(),
    title: title || 'Base map',
  });
}

const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Community tile servers with small capacity: fine for personal use, needs a
// caching proxy before any real traffic.
function rasterLayer(title, url, maxZoom, attributions) {
  return new TileLayer({
    source: new XYZ({url, maxZoom, attributions}),
    title,
  });
}

export function createOpenTopoMapLayer(title) {
  return rasterLayer(title || 'OpenTopoMap',
    'https://tile.opentopomap.org/{z}/{x}/{y}.png', 17,
    ['&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)', OSM_ATTR]);
}

export function createOpenHikingMapLayer(title) {
  return rasterLayer(title || 'OpenHikingMap',
    'https://maps.refuges.info/hiking/{z}/{x}/{y}.png', 18,
    ['&copy; <a href="https://wiki.openstreetmap.org/wiki/Hiking/mri">sly</a>', OSM_ATTR]);
}

export function createSatelliteLayer(title) {
  return rasterLayer(title || 'Satellite', env.arcgis.urlTemplate, 19, [env.arcgis.attribution]);
}

// Waymarked Trails: transparent overlay of OSM hiking route relations.
export function createHikingRoutesLayer(title) {
  return rasterLayer(title || 'hiking routes',
    'https://tile.waymarkedtrails.org/hiking/{z}/{x}/{y}.png', 18,
    ['&copy; <a href="https://waymarkedtrails.org">Waymarked Trails</a>', OSM_ATTR]);
}

export function createDebugLayer(title) {
  return new TileLayer({
    source: new TileDebug({
      projection: 'EPSG:3857',
      tileGrid: createXYZ({ maxZoom: 21 }),
    }),
    title: title || 'debug',
  });
}

export function createHillshade(opts) {
  const transp = opts && opts.transp ? '?transp=1' : '';
  const source = new XYZ({
    url: apiUrl(`/terrain/{z}/{x}/{y}.img${transp}`),
    crossOrigin: 'anonymous',
    transition: 0,
    tileGrid: TILE_GRID,
  });
  const layer = new TileLayer({
    source: source,
    opacity: 0.3,
    preload: 1,
    title: (opts && opts.title) || 'hillshade',
  });
  return { source, layer };
}

export function createColormapLayer(title) {
  const source = new XYZ({
    url: apiUrl(`/color-relief/{z}/{x}/{y}.img`),
    crossOrigin: 'anonymous',
    transition: 0,
    tileGrid: TILE_GRID,
  });
  const layer = new TileLayer({
    source: source,
    opacity: 0.8,
    preload: 1,
    title: title || 'colormap',
  });
  return { source, layer };
}

export function createTerraSource() {
  return new XYZ({
    url: apiUrl(`/terra/{z}/{x}/{y}.img`),
    crossOrigin: 'anonymous',
    transition: 0,
    interpolate: false,
    tileGrid: TILE_GRID,
  });
}

const lineStyle = new Style({
  stroke: new Stroke({ color: 'rgba(49, 159, 211, 0.8)', width: 0.8 }),
});
const indexLineStyle = new Style({
  stroke: new Stroke({ color: '#2b7fb0', width: 1.6 }),
});

// 'auto' picks the interval from the zoom of each contour tile, so close in
// there is a line every 10 m and far out every 500 m. Tiles stop at zoom 15.
const AUTO_INTERVALS = {15: 10, 14: 20, 13: 50, 12: 100, 11: 200};
const CONTOUR_MAX_ZOOM = 15;

export function contourInterval(interval, tileZoom) {
  if (interval !== 'auto') return Number(interval);
  return AUTO_INTERVALS[Math.min(tileZoom, CONTOUR_MAX_ZOOM)] || 500;
}

/**
 * Contour lines from the server. interval is metres between lines or 'auto'.
 * Index lines, bolder and labelled, come every 100 m (every 50 m at a 10 m
 * interval, every line from 100 m up).
 */
export function createContoursLayer(interval, opts) {
  const tileGrid = createXYZ({ minZoom: 3, maxZoom: CONTOUR_MAX_ZOOM });
  let current = interval;

  function tileUrl(tileCoord) {
    const [z, x, y] = tileCoord;
    return apiUrl(`/contours/${z}/${x}/${y}.mvt?interval=${contourInterval(current, z)}`);
  }

  const source = new VectorTile({
    tileUrlFunction: tileUrl,
    format: new MVT(),
    tileGrid: tileGrid,
    attributions: ['<br>Contours derived from: <a href="https://github.com/tilezen/joerd/blob/master/docs/attribution.md">Licence</a>'],
  });

  const labelCache = {};
  function labelStyle(elev) {
    if (!labelCache[elev]) {
      labelCache[elev] = new Style({
        text: new Text({
          text: elev.toString(),
          font: '9px Calibri,sans-serif',
          placement: 'line',
          overflow: true,
          fill: new Fill({ color: '#1d4f6e' }),
          stroke: new Stroke({ color: '#fff', width: 3 }),
        }),
      });
    }
    return labelCache[elev];
  }

  const layer = new VectorTileLayer({
    title: (opts && opts.title) || 'contours',
    source: source,
    style: function (feature, resolution) {
      const elev = feature.getProperties()['elevation'];
      const step = contourInterval(current, tileGrid.getZForResolution(resolution));
      const every = step >= 100 ? step : Math.min(step * 5, 100);
      if (Math.abs(elev % every) > 1e-6) return lineStyle;
      return [indexLineStyle, labelStyle(elev)];
    },
    declutter: true,
  });

  function updateInterval(intv) {
    current = intv;
    source.setTileUrlFunction(tileUrl, String(intv));
  }

  return { source, layer, updateInterval };
}
